import React from "react";
import { ListGroup } from "react-bootstrap";
import { FaIcon as Fa } from "lib/FaIcon";
import { _t } from "app/i18n/i18n";
import { isIntranetPath, getPathname } from "lib/routing";
import { isAdminUser, isInternalUser } from "lib/entitlements";
import { addPubToReadingList } from "pages/ReadingList/api/readingListApi";
import { features } from "app/features";
import { audit } from "lib/analytics/analytics";
import { auditShareSubscriptionLink } from "lib/analytics/auditEvents";
import { useSelector } from "react-redux";
import { isPDFReport } from "utils/publications";
import { DOMICILE } from "../../redux/slices/api/initialisePreferences";
import nomuchatIcon from "./images/favicon.ico";

export function ShareContextMenu({ publication, handleHide, onCopytextButtonClick, onDownloadPdfClick = () => {} }) {
  const domicile = useSelector((state) => state.preferences[DOMICILE])[0];
  return (
    <ListGroup className="m-0">
      {features.contextMenu.backIssues && (
        <ListGroup.Item>
          <Fa icon="fa fa-layer-group" className="fa-2x mr-2" />
          Back Issues
        </ListGroup.Item>
      )}

      {features.contextMenu.relatedReports && (
        <ListGroup.Item>
          <Fa icon="fas fa-sitemap" className="fa-2x mr-2" />
          Related Research
        </ListGroup.Item>
      )}

      {features.contextMenu.fromSameAuthor && (
        <ListGroup.Item>
          <Fa icon="fas fa-chalkboard-teacher" className="fa-2x mr-2" />
          Reports by same authors
        </ListGroup.Item>
      )}

      {features.contextMenu.analystBiography && (
        <ListGroup.Item>
          <Fa icon="fas fa-user-tie" className="fa-2x mr-2" />
          Author biography
        </ListGroup.Item>
      )}

      {!isOnReadingListPage() && (
        <ListGroup.Item>
          <button
            onClick={() => {
              addPubToReadingList(publication);
              document.body.click();
            }}
            type="button"
            className="bg-white border-0 p-0 m-0"
          >
            <Fa icon="fas fa-history" className="fa-2x mr-2" />
            {_t("Add to Reading List")}
          </button>
        </ListGroup.Item>
      )}

      {isAdminUser() && isIntranetPath() ? (
        <a
          href={`/research/v1/admin/ViewPublications/ViewPublicationDetails.aspx?id=${publication.id}&appname=GRPUI`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ListGroup.Item>
            <Fa icon="fas fa-user-shield" className="fa-2x mr-2" />
            {_t("Publication Admin")}
          </ListGroup.Item>
        </a>
      ) : null}

      {publication.htmlUrl && publication.pdfUrl && isPDFReport(publication) && (
        <a href={publication.pdfUrl} target="_blank" rel="noopener noreferrer">
          <ListGroup.Item>
            <Fa icon="fas fa-file-pdf" className="fa-2x mr-2" />
            {_t("Open PDF")}
          </ListGroup.Item>
        </a>
      )}

      {features.contextMenu.downloadPdf && isPDFReport(publication) && (
        <ListGroup.Item onClick={onDownloadPdfClick} style={{ cursor: "pointer" }}>
          <Fa icon="fa fa-download" className="fa-2x mr-2" />
          {_t("Save PDF")}
        </ListGroup.Item>
      )}
      <a href={generateMailTo(publication)}>
        <ListGroup.Item>
          <Fa icon="fas fa-share-alt" className="fa-2x mr-2" />
          {_t("Share...")}
        </ListGroup.Item>
      </a>
      {features.subscriptionButton && features.ShareSusbscriptionLink && (
        <a
          href={generateSubscriptionMailTo(publication)}
          onClick={() => auditShareSubscriptionLink(publication?.id, "report-context-menu")}
        >
          <ListGroup.Item>
            <Fa icon="fas fa-link" className="fa-2x mr-2" />
            {_t("share-subscription-link")}
          </ListGroup.Item>
        </a>
      )}
      {isInternalUser() && features.contextMenu.copyText && domicile === "ja" && (
        <ListGroup.Item onClick={() => handleHide()}>
          <button
            className="copy-text-anchor btn btn-link text-left p-0 text-dark cursor-pointer border-0 bg-transparent font-size-inherit"
            onClick={onCopytextButtonClick}
          >
            <img width="28px" height="28px" className="fa-2x mr-2" src={nomuchatIcon} />

            {_t("Summarize with Nomuchat")}
          </button>
        </ListGroup.Item>
      )}
    </ListGroup>
  );
}

const generateSubscriptionMailTo = (publication) => {
  const link = `${window.location.origin}${window.location.pathname}?q=${publication.id}&subscribe=true`;
  const body = encodeURIComponent(link);
  const subject = encodeURIComponent("Subscribe to Nomura Research");
  return `mailto:?body=${body}&subject=${subject}`;
};

const generateMailTo = (publication) => {
  audit(publicationShareEvent(publication));

  const body = encodeURIComponent(`${publication.title}\n${publication.pubUrl}`);
  const subject = encodeURIComponent(`[Nomura Research] ${publication.title}`);
  return `mailto:?body=${body}&subject=${subject}`;
};

const publicationShareEvent = (publication) => ({ type: "publication-share", payload: { pid: publication.id } });
const isOnReadingListPage = () => getPathname().includes("Reading_List");
